import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ActivityService } from "../../../services/user/activity.services";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useFormik } from "formik";
import { PriorityService } from "../../../services/user/priorities.services";
import { UserServices } from "../../../services/user/user.services";
import { StatusService } from "../../../services/user/statuses.services";
import { Editor } from "@tinymce/tinymce-react";
import { LeadsService } from "../../../services/user/leads.services";
import { ProbabilityService } from "../../../services/user/probability.services";
import { ItemsService } from "../../../services/user/items.services";
import striptags from "striptags";
import Select from 'react-select';
import { getDateFormat, parseCultureDate } from "../../../utility";

const AddActivityComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const activityId = location.state?.activityId || 0;
    const isEditMode = !!activityId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);
    const [priority, setPriority] = useState([]);
    const [users, setUsers] = useState([]);
    const [demoUser, setDemoUser] = useState([]);
    const [item, setItem] = useState([]);
    const [leads, setLeads] = useState([]);
    const [probability, setProbability] = useState([]);
    const [activityStatuses, setActivityStatuses] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);

    const activityTypeOptions = [
        { value: 'Call', label: 'Call' },
        { value: 'E-mail', label: 'E-mail' },
        { value: 'Meeting', label: 'Meeting' },
    ];

    const relatedToOptions = [
        { value: 'Lead', label: 'Lead' },
        { value: 'Quotes', label: 'Quotes' },
        { value: 'Invoice', label: 'Invoice' },
    ];

    const formik = useFormik({
        initialValues: {
            activity: activityTypeOptions[0].value,
            relatedTo: relatedToOptions[0].value,
            leadId: 0,
            leadStatusId: 0,
            leadProbabilityId: 0,
            activityDate: new Date(),
            followUpDate: new Date(),
            demoDate: new Date(),
            priorityId: 0,
            activityStatusId: 0,
            assignedUserId: 0,
            demoByUserId: 0,
            itemId: 0,
            competition: "",
            followUp: false,
            buyback: false,
            isDemoRequired: false,
            existingModel: "",
            activitySubject: "",
            demoRemark: "",
            demoReqsubject: "",
            description: "",
            referenceNo: "LEAD/24-25/964",
            storeId: selectedStore,
        },

        onSubmit: async (values) => {
            try {
                // console.log(values);
                let clearDescription = striptags(values.description);
                setLoading(true)
                const data = {
                    ...values,
                    activityDate: values.activityDate ? new Date(values.activityDate).toISOString() : null,
                    followUpDate: values.followUpDate ? new Date(values.followUpDate).toISOString() : null,
                    demoDate: values.demoDate ? new Date(values.demoDate).toISOString() : null,
                    activityStatusId: Number(values.activityStatusId),
                    assignedUserId: Number(values.assignedUserId),
                    demoByUserId: Number(values.demoByUserId),
                    priorityId: Number(values.priorityId),
                    leadId: Number(values.leadId),
                    itemId: Number(values.itemId),
                    isDemoRequired: values.isDemoRequired === true ? true : false,
                    buyback: values.buyback === true ? true : false,
                    followUp: values.followUp === true ? true : false,
                    description: clearDescription,
                };
                if (isEditMode) {
                    await ActivityService.updateActivityById(activityId, data).then((response) => {
                        if (response) {
                            navigate("/activity");
                        }
                    });
                } else {
                    await ActivityService.addNewActivity(data).then((response) => {
                        if (response) {
                            navigate("/activity");
                        }
                    });
                }
            } catch (error) {
                handleNotify(
                    "An error occurred while saving the activity.",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchActivityDetail = async () => {
                try {
                    setLoading(true);
                    const data = await ActivityService.getActivityById(activityId);
                    formik.setValues({
                        activityId: data?.activityId,
                        activity: data?.activity,
                        relatedTo: data?.relatedTo,
                        leadId: data?.leadId,
                        activityDate: parseCultureDate(data?.activityDate),
                        followUpDate: parseCultureDate(data?.followUpDate),
                        demoDate: parseCultureDate(data?.demoDate),
                        followUp: data?.followUp,
                        buyback: data?.buyback,
                        isDemoRequired: data?.isDemoRequired,
                        activitySubject: data?.activitySubject,
                        description: data?.description,
                        referenceNo: data?.referenceNo,
                        competition: data?.competition,
                        existingModel: data?.existingModel,
                        demoRemark: data?.demoRemark,
                        demoReqsubject: data?.demoReqsubject,
                        leadProbabilityId: data?.leads?.probabilityId,
                        leadStatusId: data?.leadStatusId,
                        activityStatusId: data?.activityStatusId,
                        assignedUserId: data?.assignedUserId,
                        priorityId: data?.priorityId,
                        demoByUserId: data?.demoByUserId,
                        itemId: data?.itemId,
                    });
                    const lead = leads.find(lead => lead.leadId === data?.leadId);
                    if (lead) {
                        setLeadStatuses(lead.statuses || []);
                        formik.setFieldValue("statusId", lead.statusId);

                        if (!formik.values.leadStatusId) {
                            formik.setFieldValue("leadStatusId", data?.leads?.statusId);
                        }
                        if (!formik.values.leadProbabilityId) {
                            formik.setFieldValue("leadProbabilityId", data?.leads?.probabilityId);
                        }
                    }
                } catch (error) {
                    handleNotify(
                        "Error fetching activity details.",
                        TOASTER_TYPE.ERROR,
                        TOASTER_POSITION.TOP_RIGHT
                    );
                }
                setLoading(false);
            };
            fetchActivityDetail();
        }
    }, []);

    const handleBackClick = () => {
        navigate("/activity");
    };
    // Fetch Data Function
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [
                    leadResponse,
                    statusResponse1,
                    probabilityResponse,
                    priorityResponse,
                    userResponse,
                    itemResponse,
                    statusResponse2
                ] = await Promise.all([
                    LeadsService.getAllLeads(),
                    StatusService.getAllStatuses(),
                    ProbabilityService.getAllProbabilities(),
                    PriorityService.getAllPriorities(),
                    UserServices.getAllUsers(),
                    ItemsService.getAllItems(),
                    StatusService.getAllStatuses(),
                ]);

                const leadOptions = leadResponse.map(lead => ({
                    value: lead.leadId,
                    label: lead.companyName,
                    statusId: lead.statusId,
                    probabilityId: lead.probabilityId,
                }));
                const leadStatusOptions = statusResponse1.filter(status => status.statustype === "Leads")
                    .map(status => ({
                        value: status.statusId,
                        label: status.statusName
                    }));
                const probabilityOptions = probabilityResponse.map(probability => ({
                    value: probability.probabilityId,
                    label: probability.probabilityName
                }));
                const priorityOptions = priorityResponse.filter(priority => priority.priorityType === "Activity").map(priority => ({
                    value: priority.priorityId,
                    label: priority.priorityName
                }));
                const userOptions = userResponse.map(user => ({
                    value: user.userId,
                    label: user.username,
                }));
                const itemOptions = itemResponse.map(item => ({
                    value: item.itemId,
                    label: item.itemName,
                }));
                const activityStatusOptions = statusResponse2.filter(status => status.statustype === "Activity")
                    .map(status => ({
                        value: status.statusId,
                        label: status.statusName,
                    }));
                setLeads(leadOptions);
                setLeadStatuses(leadStatusOptions);
                setProbability(probabilityOptions);
                setPriority(priorityOptions);
                setUsers(userOptions);
                setItem(itemOptions);
                setActivityStatuses(activityStatusOptions);

                if (isEditMode) {
                    const activityData = await ActivityService.getActivityById(activityId);
                    const leads = leadOptions.find(l => l.value === activityData.leadId);

                    formik.setValues({
                        ...activityData,
                        activityDate: parseCultureDate(activityData?.activityDate),
                        // activityDate: activityData.activityDate.split('T')[0],
                        // followUpDate: parseCultureDate(activityData?.followUpDate),
                        // demoDate: parseCultureDate(activityData?.demoDate),
                        leadStatusId: activityData.leadStatusId || leadStatusOptions[0]?.value,
                        leadProbabilityId: activityData.leadProbabilityId || probabilityOptions[0]?.value,
                        priorityId: activityData.priorityId || priorityOptions[0]?.value,
                        assignedUserId: activityData.assignedUserId || userOptions[0]?.value,
                        itemId: activityData.itemId || itemOptions[0]?.value,
                        activityStatusId: activityData.activityStatusId || activityStatusOptions[0]?.value,
                    })
                    // default values set
                    const selectedLead = leadOptions.find(lead => lead.value === activityData.leadId);
                    if (selectedLead) {
                        formik.setFieldValue('leadStatusId', selectedLead.statusId || leadStatusOptions[0]?.value);
                        formik.setFieldValue('leadProbabilityId', selectedLead.probabilityId || probabilityOptions[0]?.value);
                    }
                }
                else {
                    formik.setFieldValue('leadId', leadOptions[0]?.value);
                    formik.setFieldValue('leadStatusId', leadStatusOptions[0]?.value);
                    formik.setFieldValue('leadProbabilityId', probabilityOptions[0]?.value);
                    formik.setFieldValue('priorityId', priorityOptions[0]?.value);
                    formik.setFieldValue('assignedUserId', userOptions[0]?.value);
                    formik.setFieldValue('itemId', itemOptions[0]?.value);
                    formik.setFieldValue('activityStatusId', activityStatusOptions[0]?.value);
                }

            } catch (error) {
                handleNotify(
                    "Error fetching data:", error,
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );

            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);
    //handleLeadChange
    const handleLeadChange = (selectedOption) => {
        const selectedLead = leads.find(lead => lead.value === selectedOption.value);

        formik.setFieldValue('leadId', selectedOption.value);

        if (selectedLead) {
            const statusId = selectedLead.statusId || '';
            const probabilityId = selectedLead.probabilityId || '';
            formik.setFieldValue('leadStatusId', statusId);
            formik.setFieldValue('leadProbabilityId', probabilityId);
        }
    };
    // // Fetch demo users
    // useEffect(() => {
    //     const fetchDemoUsers = async () => {
    //         try {
    //             const userResponse = await UserServices.getAllUsers();
    //             const demoByUserOption = userResponse.map(user => ({
    //                 value: user.userId,
    //                 label: `${user.username}(${user.role.roleName})`
    //             }));
    //             setDemoUser(demoByUserOption);

    //             if (!formik.values.demoByUserId && demoByUserOption.length > 0) {
    //                 formik.setFieldValue("demoByUserId", demoByUserOption[0].value);
    //             }

    //             if (!formik.values.isDemoRequired) {
    //                 formik.setFieldValue("demoByUserId", null);
    //             }
    //         } catch (error) {
    //             handleNotify(
    //                 "Error fetching demo users:", error,
    //                 TOASTER_TYPE.ERROR,
    //                 TOASTER_POSITION.TOP_RIGHT
    //             );
    //         }
    //     };
    //     fetchDemoUsers();
    // }, []);
    // Fetch Users
    const fetchUsersData = async () => {
        const userResponse = await UserServices.getAllUsers(selectedStore);
        const sortedUsers = userResponse.sort((a, b) => a.userId - b.userId);

        const userOptions = sortedUsers.map(user => ({
            value: user.userId,
            label: `${user.username} (${user.role.roleName})`,
        }));

        setUsers(userOptions);

        // Set default if none already selected
        if (!formik.values.assignedUserId && userOptions.length > 0) {
            formik.setFieldValue('assignedUserId', userOptions[0].value);
        }
    };

    // Fetch Demo Users
    const fetchDemoUsersData = async () => {
        const userResponse = await UserServices.getAllUsers(selectedStore);
        const sortedDemoUsers = userResponse.sort((a, b) => a.userId - b.userId);

        const demoOptions = sortedDemoUsers.map(user => ({
            value: user.userId,
            label: `${user.username} (${user.role.roleName})`,
        }));

        setDemoUser(demoOptions);

        if (!formik.values.demoByUserId && demoOptions.length > 0) {
            formik.setFieldValue('demoByUserId', demoOptions[0].value);
        }
    };

    useEffect(() => {
        fetchUsersData();
        fetchDemoUsersData();
    }, []);

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Activities
                    </div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Activity" : "New Activity"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body form-container">
                        <form onSubmit={formik.handleSubmit} className="mt-4">
                            {/* Activity Name and Related To */}
                            <div className="row mb-4">
                                <div className="col-md-2 col-12">
                                    <label htmlFor="activityDate" className="form-label d-flex">
                                        Activity Date:
                                    </label>
                                    <div className="d-flex">
                                        {/* <DatePicker
                                            selected={formik.values.activityDate}
                                            onChange={(date) => formik.setFieldValue("activityDate", date)}
                                            onBlur={formik.handleBlur}
                                            name="activityDate"
                                            dateFormat={getDateFormat()}
                                            //dateFormat="dd-MM-yyyy"
                                            className={`form-control ${formik.touched.activityDate && formik.errors.activityDate ? 'is-invalid' : ''}`}
                                            id="activityDate"
                                        /> */}
                                        <DatePicker
                                            selected={parseCultureDate(formik.values.activityDate)}
                                            onChange={(date) => formik.setFieldValue("activityDate", date)}
                                            onBlur={formik.handleBlur}
                                            name="activityDate"
                                            dateFormat={getDateFormat()}
                                            className={`form-control ${formik.touched.activityDate && formik.errors.activityDate ? 'is-invalid' : ''}`}
                                            id="activityDate"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="activity" className="form-label d-flex">
                                        Activity:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="activity"
                                            name="activity"
                                            options={activityTypeOptions}
                                            value={activityTypeOptions.find(option => option.value === formik.values.activity)}
                                            onChange={selectedOption => formik.setFieldValue('activity', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 col-12">
                                    <label htmlFor="relatedTo" className="form-label d-flex">
                                        Related To:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="relatedTo"
                                            name="relatedTo"
                                            options={relatedToOptions}
                                            value={relatedToOptions.find(option => option.value === formik.values.relatedTo)}
                                            onChange={selectedOption => formik.setFieldValue('relatedTo', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Reference Name and Reference No */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="leadId" className="form-label d-flex">
                                        Name:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="leadId"
                                            name="leadId"
                                            options={leads}
                                            value={leads.find(option => option.value === formik.values.leadId)}
                                            onChange={handleLeadChange}
                                        // onChange={selectedOption => formik.setFieldValue('leadId', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 col-12">
                                    <label htmlFor="referenceNo" className="form-label d-flex">
                                        Reference No:
                                    </label>
                                    <select
                                        id="referenceNo"
                                        name="referenceNo"
                                        className="form-select"
                                        value={formik.values.referenceNo}
                                        onChange={formik.handleChange}
                                    >
                                        <option value="LEAD/24-25/964">LEAD/24-25/964</option>
                                        <option value="LEAD/24-25/965">LEAD/24-25/965</option>
                                    </select>
                                </div>
                            </div>
                            {/* Activity Date and Priority */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="priorityId" className="form-label d-flex">
                                        Priority:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="priorityId"
                                            name="priorityId"
                                            options={priority}
                                            value={priority.find(option => option.value === formik.values.priorityId)}
                                            onChange={selectedOption => formik.setFieldValue('priorityId', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 col-12">
                                    <label htmlFor="assignedUserId" className="form-label d-flex">
                                        Assigned To:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="assignedUserId"
                                            name="assignedUserId"
                                            options={users}
                                            value={users.find(option => option.value === formik.values.assignedUserId)}
                                            onChange={selectedOption => formik.setFieldValue('assignedUserId', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Status, Subject */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="statusId" className="form-label d-flex">
                                        Status:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="activityStatusId"
                                            name="activityStatusId"
                                            options={activityStatuses}
                                            value={activityStatuses.find(option => option.value === formik.values.activityStatusId)}
                                            onChange={(selectedOption) => formik.setFieldValue('activityStatusId', selectedOption.value)}
                                            menuPortalTarget={document.body}
                                            placeholder="Select Activity Status"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 col-12">
                                    <label htmlFor="activitySubject" className="form-label d-flex">
                                        Subject:
                                    </label>
                                    <input
                                        type="text"
                                        id="activitySubject"
                                        name="activitySubject"
                                        className="form-control"
                                        placeholder="Enter activitySubject"
                                        value={formik.values.activitySubject}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            </div>
                            {/* Follow-up, Buyback, Demo Required (Radio Buttons) */}
                            <div className="row mb-4 mt-5">
                                <div className="col-md-4 col-12">
                                    <div className="d-flex align-items-center mb-4">
                                        <label className="form-label me-3">Follow up? :</label>
                                        <div className="form-check form-check-inline me-3">
                                            <input
                                                type="radio"
                                                id="followUpYes"
                                                name="followUp"
                                                className="form-check-input"
                                                value={true}
                                                onChange={() => formik.setFieldValue("followUp", true)}
                                                checked={formik.values.followUp === true}
                                            />
                                            <label className="form-check-label" htmlFor="followUpYes">
                                                Yes
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                type="radio"
                                                id="followUpNo"
                                                name="followUp"
                                                className="form-check-input"
                                                value={false}
                                                checked={formik.values.followUp === false}
                                                onChange={() => formik.setFieldValue("followUp", false)}
                                            />
                                            <label className="form-check-label" htmlFor="followUpNo">
                                                No
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                {/* Buyback */}
                                <div className="col-md-4 col-12">
                                    <div className="d-flex align-items-center mb-4">
                                        <label className="form-label me-3">Buyback? :</label>
                                        <div className="form-check form-check-inline me-3">
                                            <input
                                                type="radio"
                                                id="buybackYes"
                                                name="buyback"
                                                className="form-check-input"
                                                value={true}
                                                checked={formik.values.buyback === true}
                                                onChange={() => formik.setFieldValue("buyback", true)}
                                            />
                                            <label className="form-check-label" htmlFor="active">
                                                Yes
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                type="radio"
                                                id="buybackNo"
                                                name="buyback"
                                                className="form-check-input"
                                                value={false}
                                                checked={formik.values.buyback === false}
                                                onChange={() => formik.setFieldValue("buyback", false)}
                                            />
                                            <label className="form-check-label" htmlFor="inactive">
                                                No
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                {/* Demo Required */}
                                <div className="col-md-4 col-12">
                                    <div className="d-flex align-items-center mb-4">
                                        <label className="form-label me-3">Demo Required? :</label>
                                        <div className="form-check form-check-inline me-3">
                                            <input
                                                type="radio"
                                                id="isDemoRequiredYes"
                                                name="isDemoRequired"
                                                className="form-check-input"
                                                value={true}
                                                checked={formik.values.isDemoRequired === true}
                                                onChange={() => formik.setFieldValue("isDemoRequired", true)}
                                            />
                                            <label className="form-check-label" htmlFor="active">
                                                Yes
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                type="radio"
                                                id="isDemoRequiredNo"
                                                name="isDemoRequired"
                                                className="form-check-input"
                                                value={false}
                                                checked={formik.values.isDemoRequired === false}
                                                onChange={() => formik.setFieldValue("isDemoRequired", false)}
                                            />
                                            <label className="form-check-label" htmlFor="inactive">
                                                No
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Follow-up Date Picker */}
                            {formik.values.followUp && (
                                <div className="col-md-4 col-12">
                                    <div className="d-flex align-items-center mb-4">
                                        <label htmlFor="followUpDate" className="form-label me-3">Follow-up Date:</label>
                                        <div className="d-flex">
                                            {/* <DatePicker
                                                selected={formik.values.followUpDate}
                                                onChange={(date) => formik.setFieldValue("followUpDate", date)}
                                                onBlur={formik.handleBlur}
                                                name="followUpDate"
                                                dateFormat={getDateFormat()}
                                                //dateFormat="dd-MM-yyyy"
                                                className={`form-control ${formik.touched.followUpDate && formik.errors.followUpDate ? 'is-invalid' : ''}`}
                                                id="followUpDate"
                                            /> */}
                                            <DatePicker
                                                selected={parseCultureDate(formik.values.followUpDate)}
                                                onChange={(date) => formik.setFieldValue("followUpDate", date)}
                                                onBlur={formik.handleBlur}
                                                name="followUpDate"
                                                dateFormat={getDateFormat()}
                                                className={`form-control ${formik.touched.followUpDate && formik.errors.followUpDate ? 'is-invalid' : ''}`}
                                                id="followUpDate"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {formik.values.buyback && (
                                <div className="col-md-6 col-12 mb-4">
                                    <label htmlFor="existingModel" className="form-label d-flex">
                                        Existing Model:
                                    </label>
                                    <input
                                        type="text"
                                        id="existingModel"
                                        name="existingModel"
                                        className="form-control"
                                        placeholder="Enter existing model"
                                        value={formik.values.existingModel}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            )}
                            {formik.values.isDemoRequired && (
                                <>
                                    <div className="col-md-6 col-12 mb-4">
                                        <label htmlFor="demoDate" className="form-label d-flex">
                                            Demo Date:
                                        </label>
                                        <div className="d-flex">
                                            {/* <DatePicker
                                                selected={formik.values.demoDate}
                                                onChange={(date) => formik.setFieldValue("demoDate", date)}
                                                onBlur={formik.handleBlur}
                                                name="demoDate"
                                                dateFormat={getDateFormat()}
                                                //dateFormat="dd-MM-yyyy"
                                                className={`form-control ${formik.touched.demoDate && formik.errors.demoDate ? 'is-invalid' : ''}`}
                                                id="demoDate"
                                            /> */}
                                            <DatePicker
                                                selected={parseCultureDate(formik.values.demoDate)}
                                                onChange={(date) => formik.setFieldValue("demoDate", date)}
                                                onBlur={formik.handleBlur}
                                                name="demoDate"
                                                dateFormat={getDateFormat()}
                                                className={`form-control ${formik.touched.demoDate && formik.errors.demoDate ? 'is-invalid' : ''}`}
                                                id="demoDate"
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-md-6 col-12">
                                            <label htmlFor="demoByUserId" className="form-label d-flex">
                                                Demo By:
                                            </label>
                                            <div className="text-start">
                                                <Select
                                                    id="demoByUserId"
                                                    name="demoByUserId"
                                                    options={demoUser}
                                                    value={demoUser.find(option => option.value === formik.values.demoByUserId)}
                                                    onChange={(selectedOption) => formik.setFieldValue('demoByUserId', selectedOption.value)}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-12">
                                            <label htmlFor="itemId" className="form-label d-flex">
                                                Demo Item:
                                            </label>
                                            <div className="text-start">
                                                <Select
                                                    id="itemId"
                                                    name="itemId"
                                                    options={item}
                                                    value={item.find(option => option.value === formik.values.itemId)}
                                                    onChange={(selectedOption) => formik.setFieldValue('itemId', selectedOption.value)}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-4">
                                        <div className="col-md-6 col-12">
                                            <label htmlFor="demoRemark" className="form-label d-flex">
                                                Demo Remark:
                                            </label>
                                            <input
                                                type="text"
                                                id="demoRemark"
                                                name="demoRemark"
                                                className="form-control"
                                                placeholder="Enter demo remark"
                                                value={formik.values.demoRemark}
                                                onChange={formik.handleChange}
                                            />
                                        </div>
                                        <div className="col-md-6 col-12">
                                            <label htmlFor="demoReqsubject" className="form-label d-flex">
                                                Subject:
                                            </label>
                                            <input
                                                type="text"
                                                id="demoReqsubject"
                                                name="demoReqsubject"
                                                className="form-control"
                                                placeholder="Enter subject"
                                                value={formik.values.demoReqsubject}
                                                onChange={formik.handleChange}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="row mt-2 mb-5">
                                <div className="col-12">
                                    <label htmlFor="description" className="form-label d-flex">
                                        Description:
                                    </label>
                                    <Editor
                                        value={formik.values.description}
                                        tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                                        init={{
                                            height: 300,
                                            menubar: true,
                                            plugins: [
                                                "advlist autolink lists link image charmap print preview anchor",
                                                "searchreplace visualblocks code fullscreen",
                                                "insertdatetime media table paste code help wordcount",
                                            ],
                                            toolbar:
                                                "undo redo | formatselect | bold italic backcolor | \
                                                alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                link image",
                                        }}
                                        onEditorChange={(content) =>
                                            formik.setFieldValue("description", content)
                                        }
                                    />
                                </div>
                            </div>
                            {isEditMode && (
                                <div className="col-12">
                                    <h2 className="fw-normal bg-light fs-4 p-3 rounded-3 mb-4 d-flex">Lead Update</h2>
                                    <div className="row mb-4">
                                        <div className="col-md-4 col-12 mb-3">
                                            <label htmlFor="leadStatusId" className="form-label d-flex">Status:</label>
                                            <div className="text-start">
                                                <Select
                                                    id="leadStatusId"
                                                    name="leadStatusId"
                                                    options={leadStatuses}
                                                    value={leadStatuses.find(option => option.value === formik.values.leadStatusId)}
                                                    onChange={(selectedOption) => {
                                                        formik.setFieldValue('leadStatusId', selectedOption.value)
                                                    }}
                                                    menuPortalTarget={document.body}
                                                    placeholder="Select Lead Status"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-12 mb-3">
                                            <label htmlFor="probabilityId" className="form-label d-flex">Probability:</label>
                                            <div className="text-start">
                                                <Select
                                                    id="leadProbabilityId"
                                                    name="leadProbabilityId"
                                                    options={probability}
                                                    value={probability.find(option => option.value === formik.values.leadProbabilityId)}
                                                    onChange={(selectedOption) => {
                                                        formik.setFieldValue('leadProbabilityId', selectedOption.value)
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4 col-12 mb-3">
                                            <label htmlFor="competition" className="form-label d-flex">Competition:</label>
                                            <input
                                                type="text"
                                                id="competition"
                                                className="form-control"
                                                value={formik.values.competition}
                                                onChange={formik.handleChange}
                                                placeholder="Enter competition name"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Submit */}
                            <hr />
                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    onClick={handleBackClick}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div >
    );
};
export default AddActivityComponent;
